import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  HostListener,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
 ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

const CS_SELECT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CustomSelectLocalSystemDirective),
  multi: true,
};

export interface CustomSelectLocalSystemOptions {
  displayText?: string;
  emptyListText?: string;
  emptySearchResultText?: string;
  addText?: string;
  searchDelay?: number;
}

/**
 * Angular 12 migration of AngularJS customSelectLocalSystem directive.
 * Searchable single-select dropdown with ngModel support, keyboard nav, optional Add button.
 * Expression equivalent: "value as label for item in collection | filter: $searchTerm"
 * Usage: bind [options], [displayKey], optional [valueKey]; use [(ngModel)] or formControlName.
 */
@Component({
    selector: 'gtw-custom-select-local-system, [custom-select-local-system]',
    providers: [CS_SELECT_VALUE_ACCESSOR],
    template: `
    <div class="dropdown custom-select" [class.disabled]="disabled" [class.open]="isOpen">
      <a
        class="dropdown-toggle"
        (click)="toggle($event)"
        [attr.aria-expanded]="isOpen"
        tabindex="0"
        (keydown.enter)="$event.preventDefault(); toggle($event)"
        (keydown.space)="$event.preventDefault(); toggle($event)"
      >
        <span class="truncate">{{ displayText }}</span>
        <b class="caret"></b>
      </a>
      <div
        class="dropdown-menu"
        [class.show]="isOpen"
        (click)="$event.stopPropagation()"
      >
        <div class="custom-select-search">
          <input
            #searchInput
            class="form-control"
            type="text"
            autocomplete="off"
            [(ngModel)]="searchTerm"
            (ngModelChange)="onSearchChange($event)"
            (keydown)="onSearchKeydown($event)"
            placeholder="Search..."
          />
        </div>
        <ul role="menu" #listEl (keydown)="onListKeydown($event)">
          @for (item of matches; track item; let i = $index) {
<li
            role="presentation"
           
            (click)="select(item)"
          >
            <a
              role="menuitem"
              tabindex="-1"
              href
              (click)="select(item); $event.preventDefault()"
              [attr.data-index]="i"
            >
              {{ getDisplayLabel(item) }}
            </a>
          </li>
}
          @if (!matches.length) {
<li class="empty-result">
            <em class="muted">
              @if (!searchTerm) {
<span>{{ emptyListText }}</span>
}
              @if (searchTerm) {
<span class="word-break">{{ getEmptySearchResultText() }}</span>
}
            </em>
          </li>
}
        </ul>
        @if (showAddButton) {
<div class="custom-select-action">
          <button type="button" class="btn btn-primary btn-block add-button" (click)="onAdd()">
            {{ addText }}
          </button>
        </div>
}
      </div>
    </div>
  `,
    styles: [`
    .dropdown.custom-select { position: relative; display: block; width: 100%; }
    .dropdown.custom-select.disabled .dropdown-toggle { cursor: not-allowed; opacity: 0.7; }
    .dropdown-toggle { display: flex; align-items: center; justify-content: space-between; width: 100%; min-height: 32px; padding: 6px 12px; border: 1px solid #ccc; border-radius: 4px; background: #fff; cursor: pointer; text-decoration: none; color: #333; box-sizing: border-box; }
    .dropdown-toggle .truncate { display: block; min-width: 0; flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .dropdown-toggle .caret { display: inline-block; margin-left: 8px; flex: 0 0 auto; vertical-align: middle; border: 4px solid transparent; border-top-color: currentColor; }
    .dropdown-menu { position: absolute; top: calc(100% + 2px); left: 0; z-index: 1050; width: 100%; min-width: 200px; max-width: min(420px, 100vw - 32px); max-height: 300px; overflow: hidden; display: none; flex-direction: column; background: #fff; border: 1px solid rgba(0,0,0,0.15); border-radius: 4px; box-shadow: 0 6px 12px rgba(0,0,0,0.175); padding: 0; margin-top: 0; box-sizing: border-box; }
    .dropdown-menu.show { display: flex; }
    .custom-select-search { padding: 8px; border-bottom: 1px solid #eee; }
    .custom-select-search .form-control { width: 100%; height: 32px; font-size: 14px; box-sizing: border-box; }
    .dropdown-menu ul { list-style: none; margin: 0; padding: 0; overflow-y: auto; max-height: 220px; }
    .dropdown-menu li a { display: block; padding: 8px 12px; cursor: pointer; text-decoration: none; color: #333; word-break: break-word; }
    .dropdown-menu li a:hover { background: #f5f5f5; }
    .dropdown-menu li.empty-result { padding: 8px 12px; }
    .dropdown-menu li.empty-result .muted { color: #999; font-style: italic; }
    .word-break { word-break: break-word; }
    .custom-select-action { padding: 8px; border-top: 1px solid #eee; }
    .add-button { font-size: 13px; }
  `],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CustomSelectLocalSystemDirective implements ControlValueAccessor, OnChanges {
  @Input() options: unknown[] = [];
  /** Key path for display (e.g. 'COLUMN_NAME' or 'LABEL') */
  @Input() displayKey = 'LABEL';
  /** Key path for model value; if not set, whole item is used */
  @Input() valueKey: string | null = null;
  @Input() placeholder = 'Select...';
  @Input() emptyListText = 'There are no items to display';
  @Input() emptySearchResultText = 'No results match "$0"';
  @Input() addText = 'Add';
  @Input() searchDelay = 300;
  @Input() disabled = false;
  @Input() showAddButton = false;

  @Output() optionSelected = new EventEmitter<unknown>();
  @Output() addClick = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('listEl') listEl?: ElementRef<HTMLUListElement>;

  isOpen = false;
  searchTerm = '';
  displayText = '';
  matches: unknown[] = [];
  focusedIndex = -1;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private innerValue: unknown = null;
  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.runSearch(this.searchTerm.trim());
      this.updateDisplayText();
    }
  }

  getEmptySearchResultText(): string {
    return this.emptySearchResultText.replace('$0', this.searchTerm);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.isOpen && target && !this.elementRef.nativeElement.contains(target)) {
      this.close();
    }
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this.syncDropdownOpenState();
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(_event: Event): void {
    const event = _event as FocusEvent;
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && this.elementRef.nativeElement.contains(nextTarget)) {
      return;
    }

    setTimeout(() => this.syncDropdownOpenState(), 0);
  }

  writeValue(value: unknown): void {
    this.innerValue = value;
    this.updateDisplayText();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  toggle(event: Event): void {
    event.preventDefault();
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.syncDropdownOpenState();
    if (this.isOpen) {
      this.searchTerm = '';
      this.runSearch('');
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 50);
    }
    this.cdr.markForCheck();
  }

  close(): void {
    this.isOpen = false;
    this.focusedIndex = -1;
    this.syncDropdownOpenState();
    this.cdr.markForCheck();
  }

  private syncDropdownOpenState(): void {
    const wrapper = this.elementRef.nativeElement.closest('.tax-sensitive-wrapper');
    if (!wrapper) return;

    const hasFocusInside = this.elementRef.nativeElement.contains(document.activeElement);
    const hasOpenDropdown = !!wrapper.querySelector('.dropdown.custom-select.open');

    if (this.isOpen || hasFocusInside || hasOpenDropdown) {
      wrapper.classList.add('dropdown-open');
      return;
    }

    wrapper.classList.remove('dropdown-open');
  }

  onSearchChange(term: string | unknown): void {
    const t = (typeof term === 'string' ? term : '').trim();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.runSearch(t);
      this.searchTimeout = null;
      this.cdr.markForCheck();
    }, t && this.searchDelay ? this.searchDelay : 0);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const k = event.key;
    if (k === 'Escape' || k === 'Tab') {
      this.close();
      return;
    }
    if (k === 'Enter' && this.matches.length > 0) {
      event.preventDefault();
      this.select(this.matches[0]);
      this.close();
      return;
    }
    if (k === 'ArrowDown' && this.matches.length > 0) {
      event.preventDefault();
      this.focusedIndex = 0;
      this.focusItem(0);
    }
  }

  onListKeydown(event: KeyboardEvent): void {
    const k = event.key;
    if (k === 'ArrowDown' || k === 'ArrowUp') {
      event.preventDefault();
      if (k === 'ArrowDown' && this.focusedIndex < this.matches.length - 1) {
        this.focusedIndex++;
        this.focusItem(this.focusedIndex);
      } else if (k === 'ArrowUp' && this.focusedIndex > 0) {
        this.focusedIndex--;
        this.focusItem(this.focusedIndex);
      } else if (k === 'ArrowUp') {
        this.focusedIndex = -1;
        this.searchInput?.nativeElement?.focus();
      }
    }
  }

  private focusItem(index: number): void {
    const el = this.listEl?.nativeElement?.querySelectorAll?.('li > a');
    if (el && el[index]) (el[index] as HTMLElement).focus();
  }

  private runSearch(term: string): void {
    const list = this.options ?? [];
    if (!term) {
      this.matches = [...list];
      return;
    }
    const lower = term.toLowerCase();
    this.matches = list.filter((item) => {
      const label = this.getDisplayLabel(item);
      return String(label ?? '').toLowerCase().indexOf(lower) !== -1;
    });
  }

  getDisplayLabel(item: unknown): string {
    if (item == null) return '';
    const key = this.displayKey;
    if (!key) return String(item);
    const val = this.getPath(item, key);
    return val != null ? String(val) : '';
  }

  private getPath(obj: unknown, path: string): unknown {
    if (obj == null || !path) return undefined;
    const parts = path.split('.');
    let current: unknown = obj;
    for (const p of parts) {
      const cur = current;
      current = cur != null && typeof cur === 'object' && Object.prototype.hasOwnProperty.call(cur, p)
        ? (cur as Record<string, unknown>)[p]
        : undefined;
    }
    return current;
  }

  getValue(item: unknown): unknown {
    if (this.valueKey) return this.getPath(item, this.valueKey);
    return item;
  }

  select(item: unknown): void {
    const value = this.getValue(item);
    this.innerValue = value;
    this.displayText = this.getDisplayLabel(item) || this.placeholder;
    this.onChange(value);
    this.onTouched();
    this.optionSelected.emit(item);
    this.close();
    this.cdr.markForCheck();
  }

  private updateDisplayText(): void {
    if (this.innerValue == null) {
      this.displayText = this.placeholder;
      return;
    }
    const list = this.options ?? [];
    const foundByRef = list.find((opt) => this.getValue(opt) === this.innerValue);
    if (foundByRef != null) {
      this.displayText = this.getDisplayLabel(foundByRef);
      return;
    }
    /* When options array is replaced (e.g. new reference), selected object may not match by reference. Use displayKey so object shows label instead of "[object Object]". */
    if (typeof this.innerValue === 'object' && this.innerValue !== null && this.displayKey) {
      this.displayText = this.getDisplayLabel(this.innerValue) || this.placeholder;
      return;
    }
    this.displayText = String(this.innerValue);
  }

  onAdd(): void {
    this.addClick.emit();
  }
}
